
import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Table, DatePicker, Row, Col, Spin, Typography, message, Button } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { getPermission } from '../../constants';

const { Option } = Select;
const { Title } = Typography;

const PurchaseOrderView = () => {
    const [form] = Form.useForm();
    const [suppliers, setSuppliers] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [status, setStatus] = useState('drafted'); // Current status
    const [statusdb, setStatusdb] = useState('');
    const { id } = useParams();

    const userPermission = getPermission(); // Fetch permissions

    const statuses = ['drafted', 'processed', 'approved', 'received', 'canceled'];
    const filteredStatuses = userPermission?.is_approve
        ? [...statuses] // If the user has approval permission, show all statuses including 'approved'
        : statuses.filter(status => status !== 'approved');

    // Fetch suppliers, products, and order details
    useEffect(() => {
        const fetchSuppliersAndProducts = async () => {
            try {
                setFetching(true);
                const [suppliersResponse, productsResponse] = await Promise.all([
                    axiosInstance.get('/vendors'),
                    axiosInstance.get('/products'),
                ]);
                setSuppliers(suppliersResponse.data.vendors);
                setProductsList(productsResponse.data.products);

                const orderResponse = await axiosInstance.get(`/purchase-orders/${id}`);
                const { supplier, order_date, order_type, order_number, items, status: orderStatus } = orderResponse.data;

                form.setFieldsValue({
                    order_number,
                    supplier: supplier ? supplier.id : '',
                    order_date: order_date ? moment(order_date) : null,
                    order_type,
                });

                setProducts(
                    items.map((prod) => ({
                        product: productsResponse.data.products.find((p) => p.id === prod.product_id) || null,
                        quantity: prod.quantity,
                        buying_price: prod.cost,
                        selling_price: prod.selling_price
                    }))
                );
                setStatus(orderStatus); // Set the current status
                setStatusdb(orderStatus);
            } catch (error) {
                message.error('Failed to fetch data.');
            } finally {
                setFetching(false);
            }
        };

        fetchSuppliersAndProducts();
    }, [id, form]);

    const handleQuantityChange = (value, index) => {
        setProducts((prevProducts) => {
            const updatedProducts = [...prevProducts];
            updatedProducts[index].quantity = parseFloat(value) || 0;
            return updatedProducts;
        });
    };

    const handleStatusChange = async () => {
        try {
            setFetching(true);

            const payload = {
                status,
                items: products.map(({ product, quantity, buying_price, selling_price, expire_date }) => ({
                    product_id: product.id,
                    quantity,
                    buying_price,
                    selling_price,
                    expire_date
                })),
            };

            const response = await axiosInstance.patch(`/purchase-orders/${id}/status`, payload);
            message.success(response.data.message);
            window.location.reload();
        } catch (error) {
            message.error('Failed to update status.');
        } finally {
            setFetching(false);
        }
    };

    // 1. Add handleExpireDateChange function
    const handleExpireDateChange = (date, index) => {
        setProducts((prevProducts) => {
            const updatedProducts = [...prevProducts];
            updatedProducts[index].expire_date = date ? date.format('YYYY-MM-DD') : null;
            return updatedProducts;
        });
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'product',
            render: (_, record) => <Input value={record.product?.name} disabled style={{ color: '#000' }} />,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (quantity, _, index) => (
                <Input
                    value={quantity}
                    disabled={status !== 'received' || statusdb === 'received'}
                    onChange={(e) =>
                        handleQuantityChange(
                            e.target.value === '' ? '' : parseFloat(e.target.value),
                            index
                        )
                    }

                    style={{ color: '#000' }}
                    step="0.00"
                    type="number"
                />
            ),
        },
        {
            title: 'Buying Price',
            dataIndex: 'buying_price',
            render: (buying_price) => <Input value={buying_price} disabled style={{ color: '#000' }} />,
        },
        {
            title: 'Selling Price',
            dataIndex: 'selling_price',
            render: (selling_price) => <Input value={selling_price} disabled style={{ color: '#000' }} />,
        },
        {
            title: 'Expire Date',
            dataIndex: 'expire_date',
            render: (expire_date, _, index) => (
                <DatePicker
                    value={expire_date ? moment(expire_date, 'YYYY-MM-DD') : null}
                    onChange={(date) => handleExpireDateChange(date, index)}
                    disabled={status !== 'received' || statusdb === 'received'}
                    format="YYYY-MM-DD"
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Total',
            render: (_, record) => (record.quantity * record.buying_price).toFixed(2),
        },
    ];
    const { totalQuantity, totalPrice } = useMemo(() => {
        let totalQty = 0;
        let totalPrc = 0;

        products.forEach(({ quantity, buying_price }) => {
            totalQty += quantity || 0;
            totalPrc += (quantity || 0) * (buying_price || 0);
        });

        return { totalQuantity: totalQty, totalPrice: totalPrc.toFixed(2) };
    }, [products]);

    return (
        <Spin spinning={fetching} tip="Loading...">
            <div>
                <Title level={3} style={{ color: '#000' }}>View Purchase Order</Title>
                <Row justify="start" align="middle" style={{ marginBottom: 20 }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                            <Select
                                value={status}
                                onChange={(value) => setStatus(value)}
                                style={{ width: '200px' }}  // Adjust width to fit horizontally
                                disabled={statusdb === 'received'} // Disable dropdown when status is 'received'
                            >
                                {filteredStatuses
                                    .filter(statusOption => {
                                        // Show 'approved' only if statusdb is 'processed'
                                        const showApproved = statusdb === 'processed' || statusOption !== 'approved';
                                        // Show 'received' only if statusdb is 'approved'
                                        const showReceived = statusdb === 'approved' || statusOption !== 'received';

                                        return showApproved && showReceived;
                                    })
                                    .map((statusOption) => (
                                        <Option key={statusOption} value={statusOption}>
                                            {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                        </Option>
                                    ))}
                            </Select>
                            <Button
                                type="primary"
                                style={{ marginTop: 0 }}  // No extra margin needed
                                onClick={handleStatusChange}
                                disabled={fetching || statusdb === 'received'} // Disable button when status is 'received'
                            >
                                Update Status
                            </Button>
                        </div>
                    </Col>
                </Row>


                <Form form={form} layout="vertical">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Order Number" name="order_number">
                                <Input disabled style={{ color: '#000' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Supplier" name="supplier">
                                <Select disabled style={{ color: '#000' }}>
                                    {suppliers.map((supplier) => (
                                        <Option key={supplier.id} value={supplier.id}>
                                            {supplier.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Order Date" name="order_date">
                                <DatePicker style={{ width: '100%', color: '#000' }} disabled />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Order Type" name="order_type">
                                <Select disabled style={{ color: '#000' }}>
                                    <Option value="local">Local</Option>
                                    <Option value="import">Import</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Table
                        dataSource={products}
                        columns={productColumns}
                        pagination={false}
                        rowKey={(record, index) => index}
                        style={{ marginBottom: '20px' }}
                        scroll={{ x: 'max-content' }}
                    />

                    <Row gutter={[16, 16]}>
                        <Col xs={12}>
                            <strong>Total Quantity:</strong> {totalQuantity}
                        </Col>
                        <Col xs={12} style={{ textAlign: 'right' }}>
                            <strong>Total Price:</strong> {totalPrice}
                        </Col>
                    </Row>
                </Form>
            </div>
        </Spin>
    );
};

export default PurchaseOrderView;

